import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { MdDelete } from "react-icons/md";
import {
  FaCircleMinus,
  FaCirclePlus,
  FaIndianRupeeSign,
  FaSpinner,
} from "react-icons/fa6";
import { useSelector } from "react-redux";
import "./pubCartBody.css";

export default function PubCartBody() {
  const [cartItems, setCartItems] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [loading, setLoading] = useState(false);
  const phoneNo = useSelector((state) => state.pubLogin.userPhone);
  const navigate = useNavigate();

  const fetchCartItems = useCallback(async () => {
    if (!phoneNo) return;
    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/fetchCartItems`,
        { phoneNo }
      );
      const items = response.data;
      setCartItems(items);

      const total = items.reduce((acc, item) => {
        return acc + item.ProductPrice * item.ProductQuantity;
      }, 0);
      setTotalAmount(total);
    } catch (error) {
      console.error("Error fetching cart items:", error.message);
      toast.error("Failed to load cart items.");
    } finally {
      setLoading(false);
    }
  }, [phoneNo]);

  const handleRemoveItem = async (productName) => {
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/removeCartItem`, {
        phoneNo,
        productName,
      });
      await fetchCartItems();
      toast.success("Item removed from cart!");
    } catch (error) {
      console.error("Error removing item:", error.message);
      toast.error("Failed to remove item.");
    }
  };

  const handleIncrementQuantity = async (productName) => {
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/incrementQuantity`, {
        phoneNo,
        productName,
      });
      await fetchCartItems();
    } catch (error) {
      console.error("Error incrementing quantity:", error.message);
    }
  };

  const handleDecrementQuantity = async (productName, currentQty) => {
    if (currentQty <= 1) return;
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/decrementQuantity`, {
        phoneNo,
        productName,
      });
      await fetchCartItems();
    } catch (error) {
      console.error("Error decrementing quantity:", error.message);
    }
  };

  const handlePlaceOrder = async () => {
    setLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/placeOrder`, {
        phoneNo,
      });
      toast.success(" Order Placed! We will contact you soon from our official number for payment and confirmation. ");
      fetchCartItems();
    } catch (error) {
      console.error("Error placing order:", error.message);
      toast.error("Failed to place the order. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCartItems();
  }, [fetchCartItems]);

  if (!phoneNo) {
    return (
      <div className="text-center mt-5">
        <h4>Please login to view your cart.</h4>
        <button
          className="btn btn-outline-primary mt-3"
          onClick={() => navigate("/publicUser")}
        >
          Go to Shop
        </button>
      </div>
    );
  }

  return (
    <div className="container-fluid px-3">
      {cartItems.length === 0 && !loading ? (
        <div className="text-center mt-5">
          <h2>Your cart is empty</h2>
          <p>There is nothing in your cart. Continue shopping?</p>
          <button
            className="btn btn-primary btn-lg"
            onClick={() => navigate("/publicUser")}
          >
            Continue Shopping
          </button>
        </div>
      ) : (
        <>
          <div className="text-center">
            {loading && (
              <div className="spinner-container my-5">
                <FaSpinner className="spinner-border spinner" />
              </div>
            )}

            {!loading && cartItems.length > 0 && (
              <div className="table-responsive mt-5">
                <table className="table table-bordered table-hover align-middle">
                  <thead className="table-dark text-center">
                    <tr>
                      <th>Product Name</th>
                      <th>Weight</th>
                      <th>Quantity</th>
                      <th>Price</th>
                      <th>Remove</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cartItems.map((item) => (
                      <tr key={item.ProductName} className="text-center">
                        <td>{item.ProductName}</td>
                        <td>{item.ProductWeight}</td>
                        <td>
                          <div className="quantity-control">
                            <FaCircleMinus
                              style={{
                                cursor:
                                  item.ProductQuantity > 1
                                    ? "pointer"
                                    : "not-allowed",
                                color:
                                  item.ProductQuantity > 1 ? "#000" : "#ccc",
                              }}
                              onClick={() =>
                                item.ProductQuantity > 1 &&
                                handleDecrementQuantity(
                                  item.ProductName,
                                  item.ProductQuantity
                                )
                              }
                            />
                            <span>{item.ProductQuantity}</span>
                            <FaCirclePlus
                              style={{ cursor: "pointer" }}
                              onClick={() =>
                                handleIncrementQuantity(item.ProductName)
                              }
                            />
                          </div>
                        </td>
                        <td>
                          <FaIndianRupeeSign />{" "}
                          {item.ProductPrice.toFixed(2)}
                        </td>
                        <td>
                          <MdDelete
                            style={{ cursor: "pointer", color: "red" }}
                            onClick={() =>
                              handleRemoveItem(item.ProductName)
                            }
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {!loading && cartItems.length > 0 && (
            <>
              <div className="text-center display-6 mt-4">
                Total (incl. tax & shipping):{" "}
                <span className="fw-bold">
                  <FaIndianRupeeSign /> {totalAmount.toFixed(2)}
                </span>
              </div>

              <div className="text-center mt-4 mb-5">
                <button
                  className="btn btn-success btn-lg rounded-pill shadow"
                  onClick={handlePlaceOrder}
                  disabled={loading}
                >
                  {loading ? (
                    <FaSpinner className="spinner-border spinner" />
                  ) : (
                    "Place Order"
                  )}
                </button>
              </div>
            </>
          )}
        </>
      )}
      <ToastContainer />
    </div>
  );
}
